import {useCurrentMaterialStorages} from '@domain/material-storage'
import {Loader} from '@hconnect/uikit/src/lib2'
import {ErrorOutline, Done} from '@mui/icons-material'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Paper,
  DialogProps,
  Chip,
  Tooltip,
  Alert,
  Switch,
  FormControlLabel,
  TextField
} from '@mui/material'
import {styled} from '@mui/material/styles'
import {Stack} from '@mui/system'
import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {useSettingsQuery} from '../../../common/providers'
import {StructureNodeUnist} from '../../../common/types'
import {useUrlParam} from '../../../routing'

import {MaterialStorages} from './MaterialStorages'
import {ProductionAssets} from './ProductionAssets'

const StyledDialog = styled(Dialog)<DialogProps>(() => ({
  '& .MuiDialog-paper': {
    width: '80%',
    maxWidth: 'none'
  }
}))

interface AddSubdomainPopupProps {
  upmId: string | null
  nodesById?: Record<string, StructureNodeUnist>
  name: string
  close: () => void
  closeAndSubmit: (values: {assetId?: number; storageId?: number; upmId: string}) => void
}

const AddSubdomainPopup: React.FC<AddSubdomainPopupProps> = ({
  upmId,
  name,
  close,
  nodesById,
  closeAndSubmit
}) => {
  const plantCode = useUrlParam('plantCode')
  const {
    data: assets,
    isLoading: isLoadingAssets,
    isFetching: isFetchingAssets
  } = useSettingsQuery('assets', [plantCode])
  const {
    data: materialStorages,
    isLoading: isLoadingStorages,
    isFetching: isFetchingStorages
  } = useCurrentMaterialStorages({plantId: plantCode})

  const [activeTab, setActiveTab] = useState(0)
  const [selectedAssetId, setSelectedAssetId] = useState<number | undefined>()
  const [selectedStorage, setSelectedStorageId] = useState<number | undefined>()
  const [alertVisible, setAlertVisible] = useState(false)

  const [isOnlyNotAttached, setIsOnlyNotAttached] = useState(false)
  const [assetNameFilter, setAssetNameFilter] = useState('')

  const [alertMessage, setAlertMessage] = useState('')
  const {t} = useTranslation()

  const isLoadingOrFetching =
    isLoadingAssets || isLoadingStorages || isFetchingAssets || isFetchingStorages

  useEffect(() => {
    const attachedAssets = assets?.filter((asset) => selectedAssetId === asset.id && asset.upmId)
    const attachedStorages = materialStorages?.filter(
      (storage) => selectedStorage === storage.id && storage.upmId
    )

    if (attachedAssets?.length || attachedStorages?.length) {
      setAlertMessage(t('plantSetup.assetsOrStoragesAlreadyAttached'))
      setAlertVisible(true)
    } else {
      setAlertVisible(false)
    }
  }, [selectedAssetId, selectedStorage, assets, materialStorages, t])

  const handleTabChange = (_: React.ChangeEvent<object>, newValue: number) => {
    setActiveTab(newValue)
  }
  const handleClose = () => {
    setSelectedStorageId(undefined)
    setSelectedAssetId(undefined)
    setActiveTab(0)
    close()
  }
  const handleCloseAndSubmit = () => {
    if (upmId && (!!selectedAssetId || selectedStorage)) {
      closeAndSubmit({assetId: selectedAssetId, storageId: selectedStorage, upmId: upmId})
    }
    handleClose()
  }
  const selectedAssetsChips = assets && !!selectedAssetId && (
    <Box>
      <h3>{t('plantSetup.selectedAsset')}</h3>
      <Box display="flex" flexWrap="wrap" gap={1}>
        {assets
          .filter((asset) => selectedAssetId === asset.id)
          .map((asset) =>
            !asset.upmId ? (
              <Chip
                key={asset.id}
                variant="outlined"
                color="success"
                label={asset.name}
                icon={<Done />}
              />
            ) : (
              <Tooltip
                key={asset.id}
                title={t('alreadyAttachedTo', {name: nodesById?.[asset.upmId]?.data.name})}
              >
                <Chip
                  variant="outlined"
                  color="warning"
                  label={asset.name}
                  icon={<ErrorOutline />}
                />
              </Tooltip>
            )
          )}
      </Box>
    </Box>
  )
  const selectedStoragesChips = !!selectedStorage && (
    <Box>
      <h3>{t('plantSetup.selectedMaterialStorage')}</h3>
      <Box display="flex" flexWrap="wrap" gap={1}>
        {materialStorages &&
          materialStorages
            .filter((storage) => selectedStorage === storage.id)
            .map((storage) =>
              !storage.upmId ? (
                <Chip
                  key={storage.id}
                  variant="outlined"
                  color="success"
                  label={storage.name}
                  icon={<Done />}
                />
              ) : (
                <Tooltip
                  key={storage.id}
                  title={t('alreadyAttachedTo', {name: nodesById?.[storage.upmId]?.data.name})}
                >
                  <Chip
                    variant="outlined"
                    color="warning"
                    label={storage.name}
                    icon={<ErrorOutline />}
                  />
                </Tooltip>
              )
            )}
      </Box>
    </Box>
  )

  if (isLoadingOrFetching) {
    return <Loader />
  }

  const isFiltered = ({upmId, name}: {upmId?: string; name: string}) => {
    const nameFilter = assetNameFilter.toLowerCase()
    const isNameMatch =
      nameFilter.length === 0 || name.toLowerCase().includes(assetNameFilter.toLowerCase())
    const isUpmIdMatch = !isOnlyNotAttached || !upmId
    return isNameMatch && isUpmIdMatch
  }

  const filteredAssets = assets?.filter(isFiltered)
  const filteredMaterialStorages = materialStorages?.filter(isFiltered)

  return (
    <StyledDialog open={!!upmId} onClose={handleClose}>
      <DialogTitle>{t('plantSetup.addSubdomainTo', {name})}</DialogTitle>
      <DialogContent>
        <Stack direction="row" alignItems="center" justifyContent="flex-start">
          <FormControlLabel
            control={
              <Switch
                checked={isOnlyNotAttached}
                onChange={(event) => setIsOnlyNotAttached(event.target.checked)}
              />
            }
            label="Show only not attached"
          />
          <TextField
            label="Name"
            variant="outlined"
            value={assetNameFilter}
            onChange={(event) => setAssetNameFilter(event.target.value)}
          />
        </Stack>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab
            label={t('plantSetup.productionAssets', {
              count: filteredAssets?.length ?? 0
            })}
          />
          <Tab
            label={t('plantSetup.materialStorages', {
              count: filteredMaterialStorages?.length ?? 0
            })}
          />
        </Tabs>
        <Stack spacing={2}>
          <Paper>
            {activeTab === 0 && (
              <ProductionAssets
                assets={filteredAssets}
                nodesById={nodesById}
                selectedAssetId={selectedAssetId}
                setSelectedAssetId={setSelectedAssetId}
              />
            )}
            {activeTab === 1 && (
              <MaterialStorages
                materialStorages={filteredMaterialStorages}
                nodesById={nodesById}
                selectedStorageId={selectedStorage}
                setSelectedStorageId={setSelectedStorageId}
              />
            )}
          </Paper>
          <Box display="flex" flexDirection="row" gap={4}>
            {selectedAssetsChips}
            {selectedStoragesChips}
          </Box>
          {alertVisible && <Alert severity="warning">{alertMessage}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handleClose()} color="secondary">
          {t('plantSetup.close')}
        </Button>
        <Button
          onClick={() => handleCloseAndSubmit()}
          color="primary"
          disabled={!selectedAssetId && !selectedStorage}
        >
          {t('plantSetup.associateAllWith', {name})}
        </Button>
      </DialogActions>
    </StyledDialog>
  )
}

export {AddSubdomainPopup}
